<template>
  <section class="section content-container">
    <div class="container is-fluid">
      <div class="island">
        <nav class="level">
          <div class="level-left"></div>
          <div class="level-right">
            <p class="level-item">
              <n-button id="create-contact-attribute-btn" variant="primary" @click="openCreateAttributeModal">
                <span>Create Attribute</span>
              </n-button>
            </p>
          </div>
        </nav>

        <DataTable
          :data="allContactAttributes"
          :columns="getColumns()"
          @cellClicked="openEditAttribute"
          class="contact-attributes-table"
        >
          <template v-slot:type="{ row }">
            <FriendlyString :value="getTypeDisplay(row.type)" />
          </template>

          <template v-slot:name="{ row }">
            <a v-if="!row.system && !row.archived" class="clickable-cell" @click="openEditAttribute(row)">
              {{ row.name }}
            </a>

            <span v-if="!row.system && row.archived">
              <span class="is-archived">{{ row.name }}</span> <span class="tag is-warning">Archived</span>
            </span>

            <span v-if="row.system">
              {{ row.name }}
            </span>
          </template>
          <template v-slot:actions="{ row }">
            <DropDownMenu v-if="!row.system && !row.archived" :go-left="true" dropDownIcon="dots">
              <a @click="showArchiveModal(row)" class="dropdown-item">
                <DeleteIcon></DeleteIcon>
                <span class="action-label">Archive</span>
              </a>
            </DropDownMenu>
          </template>
        </DataTable>
      </div>
    </div>

    <CreateAttribute v-if="showCreateAttributeModal" @cancel="closeAttributeModal" @create="closeAttributeModal" />
    <UpdateAttribute
      v-if="showUpdateAttributeModal"
      :attribute="selectedAttribute"
      @cancel="closeAttributeModal"
      @update="closeAttributeModal"
    />

    <ConfirmationModal
      id="archive-attribute-modal"
      v-if="showArchiveAttributeModal"
      confirmationTitle="Archive Contact Attribute"
      :confirmationMessage="getArchiveConfirmationMessage()"
      v-on:confirm="archiveAttribute"
      v-on:cancel="showArchiveAttributeModal = false"
    ></ConfirmationModal>
  </section>
</template>

<script lang="ts" setup>
import { ContactAttributeViewModel } from '@/messaging/models/contactAttributeViewModel';
import DataTable from '@/shared/components/dataTable/DataTable.vue';
import FriendlyString from '@/shared/components/FriendlyString.vue';
import DropDownMenu from '@/shared/components/dropDownMenu/DropDownMenu.vue';
import CreateAttribute from '@/features/settings/components/CreateAttribute.vue';
import { ContactAttributeType } from '@/messaging/models/contactAttributeType';
import UpdateAttribute from '@/features/settings/components/UpdateAttribute.vue';
import DataTableColumn from '@/shared/components/dataTable/dataTableColumn';
import ConfirmationModal from '@/shared/components/ConfirmationModal.vue';
import HtmlFormatter from '@/shared/services/htmlFormatter';
import DeleteIcon from 'vue-material-design-icons/TrashCanOutline.vue';
import { ARCHIVE_CONTACT_ATTRIBUTE } from '@/store/attributes/actionTypes';
import Toast from '@/shared/services/toast';
import { useStore } from 'vuex';
import { computed, ref } from 'vue';

const store = useStore();
const updateAttribute = (attributeId: string) => store.dispatch(ARCHIVE_CONTACT_ATTRIBUTE, attributeId);
const allContactAttributes = computed(() => store.getters.allContactAttributes);

const showCreateAttributeModal = ref(false);
const showUpdateAttributeModal = ref(false);
const showArchiveAttributeModal = ref(false);
const selectedAttribute = ref<ContactAttributeViewModel>(<ContactAttributeViewModel>{});

function openCreateAttributeModal() {
  showCreateAttributeModal.value = true;
}

function openEditAttribute(attribute: ContactAttributeViewModel) {
  selectedAttribute.value = attribute;
  showUpdateAttributeModal.value = true;
}

function closeAttributeModal() {
  showCreateAttributeModal.value = false;
  showUpdateAttributeModal.value = false;
}

function getTypeDisplay(typeValue: string) {
  return Object.keys(ContactAttributeType).filter((x) => (ContactAttributeType as any)[x] === typeValue)[0];
}

function showArchiveModal(attribute: ContactAttributeViewModel) {
  selectedAttribute.value = attribute;
  showArchiveAttributeModal.value = true;
}

async function archiveAttribute() {
  showArchiveAttributeModal.value = false;
  try {
    await updateAttribute(selectedAttribute.value.id);
    Toast.showSuccess(`Successfully archived attribute: ${selectedAttribute.value.name}`);
  } catch (err) {
    Toast.showError(err.message);
  }
}

function getArchiveConfirmationMessage() {
  return `Are you sure you want to archive attribute <b>${HtmlFormatter.escapeHtmlCharacters(selectedAttribute.value.name)}</b>?`;
}

function getColumns(): DataTableColumn[] {
  return [
    {
      display: 'Name',
      name: 'name',
      clickable: (row: ContactAttributeViewModel) => !row.system && !row.archived,
    },
    {
      display: 'Type',
      name: 'type',
    },
    {
      display: 'Description',
      name: 'description',
      wrappable: true,
    },
  ];
}
</script>
<style scoped lang="scss">
.action {
  cursor: pointer;
}

.is-archived {
  opacity: 0.7;
  margin-right: 10px;
}

.dropdown-item {
  .action-label {
    margin-left: 1em;
  }
}
</style>
